#!/usr/bin/env node

/**
 * Brick Codegen CLI
 * Simple code generation for Brick modules
 */

import { program } from "@commander-js/extra-typings";
import { readFileSync } from "fs";
import { dirname, resolve } from "path";
import pc from "picocolors";
import { fileURLToPath } from "url";
import { formatErrors, getErrorSummary } from "./errors.js";
import type { BrickCodegenCLIInput } from "./main-index.js";
import { BrickCodegen } from "./main-index.js";

const __filename = fileURLToPath(import.meta.url);
const __dirname = dirname(__filename);

// Read package.json for version
const packageJson = JSON.parse(
  readFileSync(resolve(__dirname, "..", "package.json"), "utf-8")
);

interface CLIOptions {
  debug?: boolean;
  outputIos?: string;
  outputAndroid?: string;
  platform?: string;
  projectRoot?: string;
  clean?: boolean;
}

// Logging utility
function log(message: string): void {
  console.log(`${pc.bold(pc.green("[Brick]"))} ${message}`);
}

function logError(message: string): void {
  console.error(`${pc.bold(pc.green("[Brick]"))} ${pc.red(message)}`);
}

program
  .name("brick-codegen")
  .description(
    "Brick Module Framework - React Native TurboModule Code Generator"
  )
  .version(packageJson.version)
  .option("--debug", "Enable debug logging", false)
  .option("--output-ios <path>", "Custom iOS output directory")
  .option("--output-android <path>", "Custom Android output directory")
  .option("--platform <platform>", "Target platform: ios or android")
  .option(
    "--projectRoot <path>",
    "Explicit project root (recommended for brownfield/bundled setups)"
  )
  .option(
    "--clean",
    "Clean output directories before generating code (default: true)",
    true
  )
  .option(
    "--no-clean",
    "Skip cleaning output directories before generating code"
  );

// Default action - just generate
program.action(async (options: CLIOptions) => {
  await runGenerate(options);
});

async function runGenerate(options: CLIOptions) {
  try {
    const config = await buildConfig(options);
    const codegen = new BrickCodegen(config);

    const result = await codegen.generate();

    if (result.success) {
      log("Done.");
    } else {
      // Display detailed errors with rich context
      if (result.detailedErrors && result.detailedErrors.length > 0) {
        console.error(
          `\n${pc.bold(pc.red("❌ Code generation failed with errors:\n"))}`
        );

        const formattedErrors = formatErrors(result.detailedErrors);
        console.error(formattedErrors);

        const summary = getErrorSummary(
          result.detailedErrors,
          result.moduleCount
        );
        console.error(`\n${pc.bold(pc.yellow("📊 Summary:"))}`);
        console.error(pc.yellow(summary));
      } else if (result.errors.length > 0) {
        // Fallback for non-detailed errors
        console.error(`\n${pc.bold(pc.red("❌ Errors:\n"))}`);
        for (const error of result.errors) {
          logError(error);
        }
      }

      console.error(""); // Empty line for spacing
      process.exit(1);
    }
  } catch (error) {
    logError(`${error instanceof Error ? error.message : "Unknown error"}`);
    process.exit(1);
  }
}

async function buildConfig(options: CLIOptions): Promise<BrickCodegenCLIInput> {
  const projectRoot = options.projectRoot || process.cwd();

  // Parse platform selection
  const platforms = parsePlatformOptions(options);

  return {
    projectRoot,
    outputIos: options.outputIos,
    outputAndroid: options.outputAndroid,
    debug: options.debug || false,
    platforms,
    clean: options.clean !== false, // Default to true, only false if explicitly set with --no-clean
  };
}

function parsePlatformOptions(options: CLIOptions): string[] | undefined {
  if (!options.platform) {
    return undefined; // Generate for all platforms
  }

  const platform = options.platform.trim().toLowerCase();

  // Validate platform
  if (!["ios", "android"].includes(platform)) {
    logError(
      `Invalid platform: ${platform}. Supported platforms: ios, android`
    );
    process.exit(1);
  }

  return [platform];
}

// Handle uncaught exceptions gracefully
process.on("uncaughtException", (error) => {
  logError(`Uncaught exception: ${error.message}`);
  process.exit(1);
});

process.on("unhandledRejection", (reason) => {
  logError(`Unhandled rejection: ${reason}`);
  process.exit(1);
});

// Parse CLI arguments
program.parse();
